import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
  getModelsByBrand,
  createModel,
  updateModel,
  deleteModelById,
  setCreateModel,
  setUpdateModel,
  setDeleteModelById,
} from '../../redux/actions/model';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import CustomBreadcrumb from '../../components/Breadcrumb/Breadcrumb';
import ModelModal from './subcomponents/ModelModal';

function ModelList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { brandId } = useParams();
  const location = useLocation();
  const brandName = location.state?.brandName || 'Marca';

  const listState = useSelector((state) => state.model.data);
  const createState = useSelector((state) => state.model.create);
  const updateState = useSelector((state) => state.model.update);
  const deleteState = useSelector((state) => state.model.deleteById);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getModelsByBrand(brandId));
  }, [dispatch, brandId]);

  const handleCreate = useCallback(
    (name) => {
      setLoading(true);
      dispatch(createModel(brandId, name));
    },
    [dispatch, brandId],
  );

  const handleEdit = useCallback(
    (id, name) => {
      setLoading(true);
      dispatch(updateModel(id, name));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteModelById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (model) => {
    setEditingModel(model);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingModel(null);
    setIsEditModalOpen(false);
  };

  const onModelCreate = (name) => {
    handleCreate(name);
    closeCreateModal();
  };

  const onModelEdit = (name) => {
    if (editingModel) {
      handleEdit(editingModel.id, name);
      closeEditModal();
    }
  };

  const navigateToVersions = (modelId, modelName) => {
    navigate(`/brands/${brandId}/models/${modelId}/versions`, { state:
      brandName,
      modelName,
    });
  };

  const navigateBack = () => {
    navigate('/brands');
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = `BGL - Modelos de ${brandName}`;
    refresh();
  }, [refresh, brandName]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      if (createState.status === 'success') {
        dispatch(getModelsByBrand(brandId));
      }
      clearReduxState(setCreateModel);
    }
  }, [createState, clearReduxState, dispatch, brandId]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      if (updateState.status === 'success') {
        dispatch(getModelsByBrand(brandId));
      }
      clearReduxState(setUpdateModel);
    }
  }, [updateState, clearReduxState, dispatch, brandId]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      if (deleteState.status === 'success') {
        dispatch(getModelsByBrand(brandId));
      }
      clearReduxState(setDeleteModelById);
    }
  }, [deleteState, clearReduxState, dispatch, brandId]);

  const breadcrumbItems = [
    { label: 'Marcas', path: '/brands' },
    { label: brandName, path: `/brands/${brandId}/models` },
    { label: 'Modelos', path: null },
  ];

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <CustomBreadcrumb items={breadcrumbItems} onNavigate={navigateBack} />

      <Container className="container-border p-3">
        <article>
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={openCreateModal}
          >
            <i className="fa fa-plus mr-3"></i>
            <span>Crear modelo</span>
          </Button>
          <Button
            className="float-right mb-2 mr-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Modelos de {brandName}</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Versiones</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center">
                  No hay modelos disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td>
                    <span className="badge badge-info">
                      {row.versions_count || 0} versiones
                    </span>
                  </td>
                  <td>
                    <>
                      <Button
                        className="mr-2"
                        size="sm"
                        color="primary"
                        onClick={() => navigateToVersions(row.id, row.name)}
                      >
                        <i className="fa fa-list mr-1"></i>
                        Ver Versiones
                      </Button>
                      <Button
                        className="mr-2"
                        size="sm"
                        onClick={() => openEditModal(row)}
                      >
                        <i className="fa fa-edit"></i>
                      </Button>
                      <ConfirmationModal
                        btnClass="btn btn-danger btn-sm"
                        iconClass="fa fa-trash"
                        title="Eliminar modelo"
                        message="¿Estás seguro que quieres eliminar este modelo? Esta operación no se podrá deshacer."
                        action={() => handleDelete(row.id)}
                        argument={row.id}
                        successMessage="Eliminar"
                        cancelMessage="Cancelar"
                        btnSuccessState="danger"
                        btnCancelState="secondary"
                      />
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Model Modal */}
      <ModelModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear modelo"
        onSubmit={onModelCreate}
      />

      {/* Edit Model Modal */}
      <ModelModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar modelo"
        defaultModel={editingModel}
        onSubmit={onModelEdit}
      />
    </>
  );
}

export default ModelList;
